/**
 * Turning one provider collection into records for the interval on screen.
 *
 * A provider is read only when the visible range is narrow enough to list --
 * a month of mail is four hundred rows already, and a year is not a thing
 * anyone scrolls. Wider than that and the source contributes no rows and one
 * hint saying so. Only this module knows the target range, so only it can make
 * that call; the provider module is handed a decided [from, to] and asked for
 * items.
 */

import { HGTimeRecordPlus } from "@/common/types";
import { ordinalToDate } from "@/common/unixTime";
import { pluralize } from "@/common/utils";

import { adaptItems } from "./adapt";
import { getProvider } from "./registry";
import { getAccessToken } from "./tokens";
import {
  ExternalAuthError,
  ExternalProviderModule,
  ProviderConfig,
  ProviderFetchContext,
} from "./types";

const MS_PER_DAY = 86_400_000;

export interface ExternalSource {
  collectionId: string;
  providerKey: string;
  config: ProviderConfig;
  connectionId: string | null;
}

export type ExternalSourceState =
  | "ok"
  /** No grant yet, or the grant is gone: the UI offers to connect. */
  | "auth"
  | "error"
  /** Nothing this provider covers falls inside the visible range. */
  | "empty"
  /** The range is wider than the provider will list; the band says to zoom in. */
  | "coarse"
  /** The provider key names a module this build does not have. */
  | "unsupported";

export interface ExternalSourceStatus {
  collectionId: string;
  providerKey: string;
  title: string;
  state: ExternalSourceState;
  message?: string;
  /** The item cap was reached and the list is only the first page of reality. */
  truncated?: boolean;
}

export interface ExternalLoadResult {
  records: HGTimeRecordPlus[];
  status: ExternalSourceStatus;
}

export interface ExternalLoadTarget {
  secondsRange: [bigint, bigint];
  userId: string;
}

/** A stable key over the sources, so the loader's cache notices a config edit. */
export function externalSourcesKey(sources: ExternalSource[]): string {
  if (sources.length === 0) return "";
  return sources
    .map(
      (source) =>
        `${source.collectionId}:${source.providerKey}:${source.connectionId ?? ""}:${JSON.stringify(source.config)}`,
    )
    .sort()
    .join("|");
}

/**
 * A provider's span cap, as a person would say it.
 *
 * The hint this feeds is a single sentence standing where the rows would be,
 * with nothing around it to give it scale, so it names the bound in words
 * rather than in days. Written from `limits.maxSpanDays` rather than beside
 * it, so a provider that reads a year (Calendar) and one that reads a month
 * (Gmail) need one sentence between them.
 */
function spanPhrase(days: number): string {
  if (days >= 360) return "a year";
  if (days >= 28) return "a month";
  if (days >= 7) return "a week";
  return pluralize(days, "day");
}

/** Clamp the visible range to what the provider could possibly know about. */
function windowFor(
  provider: ExternalProviderModule,
  secondsRange: [bigint, bigint],
): [Date, Date] | null {
  const from = ordinalToDate(secondsRange[0]);
  const to = ordinalToDate(secondsRange[1]);
  // Both null means the whole range predates 1970 -- a Gmail collection viewed
  // at the Jurassic, which costs no network at all.
  if (to === null) return null;

  const earliest = provider.limits.earliest ?? new Date(0);
  const start = from && from > earliest ? from : earliest;
  if (start >= to) return null;
  return [start, to];
}

export async function loadExternalSource(
  source: ExternalSource,
  target: ExternalLoadTarget,
  signal: AbortSignal,
): Promise<ExternalLoadResult> {
  const provider = getProvider(source.providerKey);
  const title = provider?.title ?? source.providerKey;

  const status = (
    state: ExternalSourceState,
    extra: Partial<ExternalSourceStatus> = {},
  ): ExternalSourceStatus => ({
    collectionId: source.collectionId,
    providerKey: source.providerKey,
    title,
    state,
    ...extra,
  });

  if (!provider) {
    return {
      records: [],
      status: status("unsupported", {
        message: `This build does not know how to read ${source.providerKey}.`,
      }),
    };
  }

  if (!source.connectionId) {
    return {
      records: [],
      status: status("auth", {
        message: `Connect ${provider.title} to see anything here.`,
      }),
    };
  }

  const range = windowFor(provider, target.secondsRange);
  if (!range) {
    return { records: [], status: status("empty") };
  }

  // Before the token, not after: a coarse view is answered without touching
  // the network at all, which is most of what panning across years costs.
  const spanDays = (range[1].getTime() - range[0].getTime()) / MS_PER_DAY;
  if (spanDays > provider.limits.maxSpanDays) {
    return {
      records: [],
      // Rendered on its own in the middle of an empty list, with nothing
      // around it to say which provider is meant -- so the sentence does.
      status: status("coarse", {
        message:
          `This range is too wide for ${provider.title}. ` +
          `Zoom in to ${spanPhrase(provider.limits.maxSpanDays)} or narrower.`,
      }),
    };
  }

  try {
    const ctx: ProviderFetchContext = {
      accessToken: await getAccessToken(source.connectionId),
      config: source.config,
      from: range[0],
      to: range[1],
      limit: provider.limits.maxItems,
      signal,
    };
    const items = await provider.fetchItems(ctx);
    const records = await adaptItems(
      items,
      source.config,
      provider.itemUrl?.bind(provider),
      {
        collectionId: source.collectionId,
        providerKey: source.providerKey,
        userId: target.userId,
        secondsRange: target.secondsRange,
      },
    );
    return {
      records,
      status: status("ok", {
        truncated: items.length >= provider.limits.maxItems,
      }),
    };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError")
      throw error;
    if (error instanceof ExternalAuthError) {
      return {
        records: [],
        status: status("auth", {
          message: `${provider.title} access expired. Reconnect to see it again.`,
        }),
      };
    }
    return {
      records: [],
      status: status("error", {
        message:
          error instanceof Error
            ? error.message
            : `Could not read ${provider.title}.`,
      }),
    };
  }
}
